/**
 * 部门管理页面
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Check,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import type { Department } from '@/types/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import { api, getErrorMessage } from '@/services/api';
import styles from './Departments.module.css';

export default function DepartmentsPage() {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();

  // 分页状态
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    dept_name: '',
    parent_id: 0,
    dept_desc: '',
  });

  // 多选相关状态
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await api.getDepartments();
      if (response.data) {
        setDepartments(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('加载部门数据失败:', error);
      showToast('error', getErrorMessage(error) || '加载部门数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortTh = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th className={styles.tableHeaderCell} style={{cursor:'pointer',userSelect:'none'}} onClick={() => handleSort(field)}>
      {children} {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  const filteredDepartments = departments.filter((dept) =>
    dept.dept_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (dept.dept_desc && dept.dept_desc.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (sortField) {
    filteredDepartments.sort((a: any, b: any) => {
      let va = a[sortField] ?? '', vb = b[sortField] ?? '';
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  // 分页计算
  const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDepartments = filteredDepartments.slice(startIndex, startIndex + itemsPerPage);

  const handleCreate = () => {
    setEditingDepartment(null);
    setFormData({ dept_name: '', parent_id: 0, dept_desc: '' });
    setShowModal(true);
  };

  const handleEdit = (department: Department) => {
    setEditingDepartment(department);
    setFormData({
      dept_name: department.dept_name,
      parent_id: department.parent_id,
      dept_desc: department.dept_desc || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (deptId: number) => {
    const confirmed = await confirmDialog.confirm({
      title: '确认删除',
      message: '确定要删除这个部门吗？此操作不可恢复。',
      confirmText: '确认删除',
      danger: true,
    });
    if (!confirmed) return;

    try {
      setDeleting(deptId);
      await api.deleteDepartment(deptId);
      setDepartments(departments.filter((d) => d.dept_id !== deptId));
      showToast('success', '部门删除成功');
    } catch (error: unknown) {
      showToast('error', getErrorMessage(error) || '删除部门失败');
    } finally {
      setDeleting(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.dept_name.trim()) {
      showToast('warning', '请输入部门名称');
      return;
    }

    setSaving(true);
    try {
      if (editingDepartment) {
        // 更新部门
        await api.updateDepartment(editingDepartment.dept_id, {
          dept_name: formData.dept_name.trim(),
          parent_id: formData.parent_id,
          dept_desc: formData.dept_desc.trim() || null,
        });
        await loadData();
        setShowModal(false);
      } else {
        // 创建新部门
        await api.createDepartment({
          dept_name: formData.dept_name.trim(),
          parent_id: formData.parent_id,
          dept_desc: formData.dept_desc.trim() || null,
        });
        await loadData();
        setShowModal(false);
      }
    } catch (error: unknown) {
      showToast('error', getErrorMessage(error) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 多选相关函数
  const toggleDepartmentSelection = (deptId: number) => {
    setSelectedDepartments(prev =>
      prev.includes(deptId)
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId]
    );
  };

  const selectAllDepartments = () => {
    if (selectedDepartments.length === paginatedDepartments.length && paginatedDepartments.length > 0) {
      // 如果当前页已全选，则取消选择当前页的所有部门
      setSelectedDepartments(prev =>
        prev.filter(id => !paginatedDepartments.some(dept => dept.dept_id === id))
      );
    } else {
      // 否则，选择当前页的所有部门
      const paginatedDeptIds = paginatedDepartments.map(d => d.dept_id);
      setSelectedDepartments(prev => {
        // 保留不在当前页但之前已选择的部门
        const otherSelected = prev.filter(id => !paginatedDeptIds.includes(id));
        // 添加当前页的所有部门
        return [...otherSelected, ...paginatedDeptIds];
      });
    }
  };

  const handleBatchDelete = async () => {
    if (selectedDepartments.length === 0) {
      showToast('warning', '请至少选择一个部门');
      return;
    }

    try {
      for (const deptId of selectedDepartments) {
        await api.deleteDepartment(deptId);
      }
      setDepartments(departments.filter(d => !selectedDepartments.includes(d.dept_id)));
      setSelectedDepartments([]);
      setIsBatchDeleteModalOpen(false);
      showToast('success', `成功删除 ${selectedDepartments.length} 个部门`);
    } catch (error: unknown) {
      showToast('error', getErrorMessage(error) || '批量删除失败');
    }
  };

  const openBatchDeleteModal = () => {
    setIsBatchDeleteModalOpen(true);
  };

  const closeBatchDeleteModal = () => {
    setIsBatchDeleteModalOpen(false);
  };

  if (loading && departments.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Loader2 className={styles.spinner} size={32} />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 页头 */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>部门管理</h1>
          <p className={styles.subtitle}>管理部门组织架构</p>
        </div>
      </div>

      {/* 搜索栏 */}
      <div className={styles.searchBar}>
        <div className={styles.searchInput}>
          <Search size={18} strokeWidth={1.5} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="搜索部门..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
              <X size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      {/* 批量操作工具栏 */}
      {hasPermission('用户管理') && selectedDepartments.length > 0 && (
        <div className={styles.batchToolbar}>
          <div className={styles.batchInfo}>
            <Check size={18} />
            已选择 <span className={styles.batchCount}>{selectedDepartments.length}</span> / {filteredDepartments.length} 个部门
          </div>
          <div className={styles.batchActions}>
            <button
              type="button"
              className={styles.selectAllBtn}
              onClick={selectAllDepartments}
            >
              {selectedDepartments.length === paginatedDepartments.length && paginatedDepartments.length > 0 ? '取消全选' : '全选'}
            </button>
            <button
              type="button"
              className={styles.batchDeleteBtn}
              onClick={openBatchDeleteModal}
            >
              <Trash2 size={16} />
              批量删除
            </button>
            <button
              type="button"
              className={styles.cancelBatchBtn}
              onClick={() => setSelectedDepartments([])}
            >
              取消选择
            </button>
          </div>
        </div>
      )}

      {/* 列表 */}
      <div className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
          <thead>
            <tr>
              {hasPermission('用户管理') && (
                <th className={`${styles.tableHeaderCell}`} style={{ width: '50px' }}>
                  <label className={styles.checkboxWrapper}>
                    <input
                      type="checkbox"
                      checked={paginatedDepartments.length > 0 && paginatedDepartments.every(dept => selectedDepartments.includes(dept.dept_id))}
                      onChange={selectAllDepartments}
                    />
                    <span className={styles.checkboxCustom}>
                      <Check size={14} strokeWidth={3} />
                    </span>
                  </label>
                </th>
              )}
              <th className={`${styles.tableHeaderCell}`} style={{ width: '18%', textAlign: 'left' }}>部门名称</th>
              <th className={`${styles.tableHeaderCell}`} style={{ width: '30%' }}>描述</th>
              <SortTh field="create_time">创建时间</SortTh>
              <SortTh field="user_count">用户数</SortTh>
              <th className={`${styles.tableHeaderCell}`} style={{ width: '12%', textAlign: 'center' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDepartments.map((dept) => {
              const isSelected = selectedDepartments.includes(dept.dept_id);
              return (
                <tr
                  key={dept.dept_id}
                  className={`${styles.tableRow} ${isSelected ? styles.selected : ''}`}
                >
                  {hasPermission('用户管理') && (
                    <td className={`${styles.tableCell} ${styles.checkbox}`}>
                      <label className={styles.checkboxWrapper}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleDepartmentSelection(dept.dept_id)}
                        />
                        <span className={styles.checkboxCustom}>
                          <Check size={14} strokeWidth={3} />
                        </span>
                      </label>
                    </td>
                  )}
                  <td className={`${styles.tableCell} ${styles.leftAlign} ${styles.cardHeaderCell}`} title={dept.dept_name} data-label="部门名称">
                    {dept.dept_name}
                  </td>
                  <td className={styles.tableCell} title={dept.dept_desc || ''} data-label="描述">
                    {dept.dept_desc ? (dept.dept_desc.length > 20 ? dept.dept_desc.substring(0, 20) + '...' : dept.dept_desc) : '-'}
                  </td>
                  <td className={styles.tableCell} data-label="创建时间">
                    {dept.create_time ? new Date(dept.create_time).toLocaleDateString('zh-CN') : '-'}
                  </td>
                  <td className={styles.tableCell} data-label="用户数" style={{textAlign:'center'}}>
                    {(dept as any).user_count ?? '-'}
                  </td>
                  <td className={`${styles.tableCell} ${styles.actions} ${styles.cardFooterCell}`} data-label="操作">
                    <div className={styles.tableActions}>
                      {hasPermission('用户管理') && (
                        <>
                          <button
                            className={styles.tableActionBtn}
                            onClick={() => handleEdit(dept)}
                            title="编辑"
                          >
                            <Edit size={16} strokeWidth={1.5} />
                          </button>
                          <button
                            className={`${styles.tableActionBtn} ${styles.danger}`}
                            onClick={() => handleDelete(dept.dept_id)}
                            title="删除"
                            disabled={deleting === dept.dept_id}
                          >
                            {deleting === dept.dept_id ? (
                              <Loader2 size={16} className={styles.spinner} />
                            ) : (
                              <Trash2 size={16} strokeWidth={1.5} />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* 翻页控件 */}
        <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          共 {filteredDepartments.length} 条记录
        </div>
          <div className={styles.paginationControls}>
            <button
              className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              首页
            </button>
            <button
              className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            >
              上一页
            </button>
            <div className={styles.pageNumbers}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  // 总页数小于等于5，显示所有页码
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  // 当前页靠近开头，显示前5页
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // 当前页靠近结尾，显示后5页
                  pageNum = totalPages - 4 + i;
                } else {
                  // 当前页在中间，显示当前页前后各两页
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`${styles.pageNumber} ${currentPage === pageNum ? styles.active : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <span className={styles.pageEllipsis}>...</span>
              )}
              {totalPages > 5 && currentPage >= totalPages - 2 && (
                <button
                  className={`${styles.pageNumber} ${currentPage === totalPages ? styles.active : ''}`}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </button>
              )}
            </div>
            <button
              className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            >
              下一页
            </button>
            <button
              className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
              onClick={() => setCurrentPage(totalPages)}
            >
              末页
            </button>
          </div>
        </div>
      </div>

      {/* 空状态 */}
      {filteredDepartments.length === 0 && !loading && (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="9" y1="9" x2="21" y2="9"/>
              <line x1="9" y1="15" x2="21" y2="15"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
          </div>
          <p className={styles.emptyText}>
            {searchQuery ? '没有找到匹配的部门' : '暂无部门'}
          </p>
          {hasPermission('用户管理') && !searchQuery && (
            <motion.button
              className={styles.createButton}
              onClick={handleCreate}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus size={18} strokeWidth={2} />
              <span>新建部门</span>
            </motion.button>
          )}
        </div>
      )}

      {/* 创建/编辑弹窗 */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>
                  {editingDepartment ? '编辑部门' : '新建部门'}
                </h2>
                <button className={styles.modalClose} onClick={() => setShowModal(false)}>
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>

              <form className={styles.modalBody} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label>部门名称 *</label>
                  <input
                    type="text"
                    placeholder="请输入部门名称"
                    value={formData.dept_name}
                    onChange={(e) =>
                      setFormData({ ...formData, dept_name: e.target.value })
                    }
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label style={{display:'none'}}>上级部门</label>
                  <select style={{display:'none'}}
                    value={formData.parent_id}
                    onChange={(e) =>
                      setFormData({ ...formData, parent_id: parseInt(e.target.value) })
                    }
                  >
                    <option value={0}>无上级部门（顶级部门）</option>
                    {departments.map((dept) => (
                      <option key={dept.dept_id} value={dept.dept_id}>
                        {dept.dept_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>部门描述</label>
                  <textarea
                    placeholder="请输入部门描述"
                    value={formData.dept_desc}
                    onChange={(e) =>
                      setFormData({ ...formData, dept_desc: e.target.value })
                    }
                    rows={3}
                  />
                </div>

                <div className={styles.modalFooter}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setShowModal(false)}
                  >
                    取消
                  </button>
                  <button type="submit" className={styles.submitButton} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 size={16} className={styles.spinner} />
                        保存中...
                      </>
                    ) : (
                      editingDepartment ? '保存更改' : '创建部门'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 批量删除确认弹窗 */}
      <AnimatePresence>
        {isBatchDeleteModalOpen && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeBatchDeleteModal}
          >
            <motion.div
              className={styles.confirmModal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.confirmIcon}>
                <Trash2 size={40} />
              </div>
              <h3 className={styles.confirmTitle}>确认批量删除</h3>
              <p className={styles.confirmMessage}>
                确定要删除选中的 <strong>{selectedDepartments.length}</strong> 个部门吗？
              </p>
              <p className={styles.confirmWarning}>
                此操作不可恢复，部门下的用户也将受到影响，请谨慎操作。
              </p>
              <div className={styles.confirmActions}>
                <button className={styles.confirmCancel} onClick={closeBatchDeleteModal}>
                  取消
                </button>
                <button
                  className={`${styles.confirmButton} ${styles.danger}`}
                  onClick={handleBatchDelete}
                >
                  确认删除
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 浮动新增按钮 */}
      {hasPermission('用户管理') && (
        <motion.button className={styles.fab} onClick={handleCreate} whileHover={{scale:1.1}} whileTap={{scale:0.9}} title="新建部门">
          <Plus size={28} strokeWidth={2} />
        </motion.button>
      )}
    </div>
  );
}